import React, { useEffect } from 'react';
import { 
  X, CheckCircle2, AlertCircle 
} from 'lucide-react';

export const formatTime = (timeStr) => {
  if (!timeStr) return '';
  if (typeof timeStr !== 'string') return timeStr;
  if (timeStr.includes('T')) {
    try {
      const date = new Date(timeStr);
      if (isNaN(date.getTime())) return timeStr;
      // If the year is 1899, it's a time-only cell from Sheets/Excel
      return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
    } catch (e) { return timeStr; }
  }
  return timeStr;
};

export const formatDate = (dateStr, type = 'month') => {
  if (!dateStr) return '';
  if (typeof dateStr !== 'string') return dateStr;
  if (dateStr.includes('T')) {
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      if (type === 'month') return date.toLocaleString('default', { month: 'short' }).toUpperCase();
      if (type === 'day') return date.getDate().toString();
      return date.toLocaleDateString();
    } catch (e) { return dateStr; }
  }
  return dateStr;
};

export const W = ({ children, style = {} }) => (
  <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 16px', ...style }}>{children}</div>
);

export const Sec = ({ children, bg = 'var(--bg)', py = 120, style = {} }) => (
  <section style={{ background: bg, padding: `${py}px 0`, ...style }}>
    <div style={{ maxWidth: 1160, margin: '0 auto', padding: '0 16px' }}>{children}</div>
  </section>
);

export const SH = ({ eyebrow, title, sub, center = true }) => (
  <div style={{ textAlign: center ? 'center' : 'left', marginBottom: 64, ...(center ? { maxWidth: 680, margin: '0 auto 64px' } : {}) }}>
    {eyebrow && <div className="eyebrow">{eyebrow}</div>}
    <h2 style={{ 
      fontFamily: 'var(--font-display)',
      fontSize: 'clamp(28px, 4vw, 44px)', 
      fontWeight: 800, 
      color: 'var(--on-surface)', 
      lineHeight: 1.1, 
      letterSpacing: 'var(--ls-tight)',
      marginBottom: 20 
    }}>{title}</h2>
    {sub && <p style={{ fontSize: 17, color: 'var(--on-surface-variant)', lineHeight: 1.6, marginTop: 16 }}>{sub}</p>}
  </div>
);

export const FormField = ({ id, label, placeholder, type = 'text', icon, value, onChange, error }) => (
  <div style={{ marginBottom: 24 }}>
    <label className="lbl" htmlFor={id}>{label}</label>
    <div className={`inp-container ${error ? 'err' : ''}`}>
      {icon && <span style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: 'var(--slate-400)', pointerEvents: 'none', zIndex: 2 }}>{icon}</span>}
      <input 
        id={id} 
        className={`inp ${icon ? 'inp-icon' : ''}`} 
        type={type} 
        placeholder={placeholder} 
        value={value} 
        onChange={e => onChange(id, e.target.value)} 
        autoComplete="off" 
      />
      <div className="inp-pulse" />
    </div>
    {error && <p style={{ fontSize: 11.5, color: 'var(--red)', marginTop: 6, fontWeight: 500 }}>{error}</p>}
  </div>
);

export const SelectField = ({ id, label, icon, value, onChange, error, options }) => (
  <div style={{ marginBottom: 24 }}>
    <label className="lbl" htmlFor={id}>{label}</label>
    <div className={`inp-container ${error ? 'err' : ''}`}>
      {icon && <span style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: 'var(--slate-400)', pointerEvents: 'none', zIndex: 2 }}>{icon}</span>}
      <select 
        id={id} 
        className={`inp ${icon ? 'inp-icon' : ''}`} 
        style={{ appearance: 'none', cursor: 'pointer' }} 
        value={value} 
        onChange={e => onChange(id, e.target.value)}
      >
        <option value="">Select Platform</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      <div className="inp-pulse" />
    </div>
    {error && <p style={{ fontSize: 11.5, color: 'var(--red)', marginTop: 6, fontWeight: 500 }}>{error}</p>}
  </div>
);

export const TextareaField = ({ id, label, placeholder, value, onChange, error, rows = 4 }) => (
  <div style={{ marginBottom: 24 }}>
    <label className="lbl" htmlFor={id}>{label}</label>
    <div className={`inp-container ${error ? 'err' : ''}`}>
      <textarea 
        id={id} 
        className="inp" 
        rows={rows} 
        placeholder={placeholder} 
        style={{ resize: 'vertical', minHeight: 120 }} 
        value={value} 
        onChange={e => onChange(id, e.target.value)} 
      />
      <div className="inp-pulse" />
    </div>
    {error && <p style={{ fontSize: 11.5, color: 'var(--red)', marginTop: 6, fontWeight: 500 }}>{error}</p>}
  </div>
);

export const Toast = ({ message, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className="toast">
      {type === 'success' ? <CheckCircle2 size={18} style={{ color: 'var(--primary)' }} /> : <AlertCircle size={18} style={{ color: 'var(--red)' }} />}
      <span style={{ flex: 1, fontSize: 13.5, fontWeight: 500 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--slate-400)', display: 'flex' }}><X size={14} /></button>
    </div>
  );
};


